import {
  colorOrange,
  colorYellow,
  colorGreen,
} from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  Bullet,
  formatDateTimeShort,
  SystemClassifierItemDto,
  Tooltip,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import translations from './ActiveProcessingCell.translations.json';
import React, { FC, memo } from 'react';
import { CellContext } from '@tanstack/react-table';

const { addTranslations, t } = i18nNsFactory('active-processing-type');
addTranslations(translations);

const LABEL_DELIMITER = ' - ';

interface ProcessingDetails {
  processingTypeId: number | null;
  processingMethodId: number | null;
  dataProcessingScopeId: number | null;
  processingTime: string | null;
  processingStartedByUsername: string | null;
}

export const getActiveProcessingTypeLabel = ({
  processingStateCode,
  processing,
  processingTypes,
  processingMethods,
  processingScopes,
}: {
  processingStateCode: string | null;
  processing: ProcessingDetails | null;
  processingTypes: Map<number, SystemClassifierItemDto>;
  processingMethods: Map<number, SystemClassifierItemDto>;
  processingScopes: Map<number, SystemClassifierItemDto>;
}) => {
  if (!processingStateCode) return '';

  const processingStateLabel = [
    t('processingState.' + processingStateCode),
    processingStateCode,
  ];

  const type =
    processing?.processingTypeId == null
      ? undefined
      : processingTypes.get(processing.processingTypeId);
  const method =
    processing?.processingMethodId == null
      ? undefined
      : processingMethods.get(processing.processingMethodId);
  const scope =
    processing?.dataProcessingScopeId == null
      ? undefined
      : processingScopes.get(processing.dataProcessingScopeId);

  return [
    ...processingStateLabel,
    type?.name,
    formatDateTimeShort(processing?.processingTime),
    processing?.processingStartedByUsername,
    t('method.' + method?.code, { scope: scope?.name }),
  ].join(LABEL_DELIMITER);
};

const getBulletColor = (code: string | null) => {
  switch (code) {
    case 'VALIDATION_IN_PROGRESS':
      return colorOrange[5];
    case 'VALIDATION_READY':
      return colorGreen[5];
    default:
      return colorYellow[5];
  }
};

export const ActiveProcessingCell: FC<CellContext<object, string>> = memo(
  (props) => {
    const delimitedLabel = props.renderValue();
    if (!delimitedLabel) return null;

    const [
      label,
      processingStateCode,
      typeName,
      startTime,
      startedByUsername,
      methodAndScope,
    ] = delimitedLabel.split(LABEL_DELIMITER);

    return (
      <Tooltip
        title={
          <>
            <strong>{typeName}</strong>
            <br />
            {startTime} ({startedByUsername})
            <br />
            {methodAndScope}
          </>
        }
        placement="bottom-start"
      >
        <Flex align="center" gap={6}>
          <Bullet color={getBulletColor(processingStateCode)} />
          {label}
        </Flex>
      </Tooltip>
    );
  },
);
